import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import addIcon from '../../../../assets/Add-icon.svg';
import MediaRenderer from '../MediaRenderer';
import { mediaAPI } from '../../../../services/api';
import stockMediaService from '../../../../services/stockMedia';
import UploadProgress from '../../../UI/UploadProgress';
import CustomDropdown from '../../../UI/CustomDropdown';

const serverBaseURL = window.location.origin.replace(':3000', ':8000');

const MediaFiles = ({
  apiUrl,
  onItemSelect,
  initialMediaType,
  isModal = false,
  onItemDoubleClick,
  searchQuery,
  activeSource = 'default', // 'default' = uploads+stock (original behavior), 'all' | 'uploads' | 'ai' | 'stock'
}) => {
  // Map initial media type if provided (for modal usage)
  const getInitialButton = () => {
    if (!initialMediaType) return 'images';
    switch (initialMediaType) {
      case 'image':
        return 'images';
      case 'video':
        return 'videos';
      case 'audio':
        return 'audio';
      case 'gif':
        return 'gifs';
      default:
        return 'images';
    }
  };

  const [activeButton, setActiveButton] = useState(getInitialButton());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'converting'
  const fileInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 1920);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const allMediaTypes = [
    { id: 'images', label: 'Images' },
    { id: 'videos', label: 'Videos' },
    { id: 'audio', label: 'Audio' },
    { id: 'gifs', label: 'GIFs' },
    { id: 'stickers', label: 'Stickers' },
  ];

  // For large screens: show Images and Videos as buttons, rest in dropdown
  const largeScreenButtons = [
    { id: 'images', label: 'Images' },
    { id: 'videos', label: 'Videos' },
  ];

  const largeScreenMoreOptions = [
    { id: 'audio', label: 'Audio' },
    { id: 'gifs', label: 'GIFs' },
    { id: 'stickers', label: 'Stickers' },
  ];

  // Determine which buttons and options to show based on screen size
  const buttons = isSmallScreen ? [] : largeScreenButtons;
  const moreOptions = isSmallScreen ? allMediaTypes : largeScreenMoreOptions;

  // Check if active button is in the "More" dropdown
  const isMoreOption = moreOptions.some(opt => opt.id === activeButton);
  const getMoreLabel = () => {
    if (isSmallScreen) {
      // On small screens, show the selected media type
      return allMediaTypes.find(opt => opt.id === activeButton)?.label || 'Select Media';
    }
    if (isMoreOption) {
      return moreOptions.find(opt => opt.id === activeButton)?.label || 'More';
    }
    return 'More';
  };

  // Helper to map button IDs to API mediaType values
  const mediaTypeMap = {
    images: 'image',
    videos: 'video',
    audio: 'audio',
    gifs: 'gif',
    stickers: 'sticker',
  };

  // Helper functions for URL resolution
  const isAbsoluteUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    return isAbsoluteUrl(url) ? url : `${serverBaseURL}${url}`;
  };

  const fetchMediaData = async (page, pageSize) => {
    // If API URL is provided, let MediaRenderer handle it
    if (apiUrl) {
      return null; // MediaRenderer will use its default API fetcher
    }

    const showUploads = activeSource === 'all' || activeSource === 'default' || activeSource === 'uploads';
    const showAi = activeSource === 'all' || activeSource === 'ai';
    const showStock = activeSource === 'all' || activeSource === 'default' || activeSource === 'stock';
    try {
      const allItems = [];
      const mediaType = mediaTypeMap[activeButton];

      // --- Single source: paginate fully ---

      // Uploads-only view: paginate uploads
      if (activeSource === 'uploads') {
        try {
          const response = await mediaAPI.getMyMedia({
            mediaType,
            page,
            limit: pageSize,
            search: searchQuery,
          });

          if (response.data?.data?.media) {
            const userMedia = response.data.data.media.map((item) => ({
              id: item._id,
              name: item.originalName,
              thumbnail: getFullUrl(item.thumbnail) || getFullUrl(item.url),
              url: getFullUrl(item.url),
              duration: item.duration,
              width: item.dimensions?.width || item.width,
              height: item.dimensions?.height || item.height,
              type: activeButton,
              category: 'My Uploads',
              description: item.description || '',
              size: item.formattedSize || item.size,
              isUserUpload: true,
              uploadedAt: item.uploadedAt,
            }));

            if (page === 1 && userMedia.length > 0) {
              allItems.push({
                id: 'header-uploads',
                isHeader: true,
                title: 'My Uploads',
                count: response.data.total || userMedia.length,
              });
            }
            allItems.push(...userMedia);

            const currentPage = response.data.page || page;
            const totalPages = response.data.pages || 1;
            return { data: allItems, hasMore: currentPage < totalPages };
          }
        } catch (error) {
          console.log('No user uploads or not authenticated');
        }
        return { data: allItems, hasMore: false };
      }

      // AI-only view: paginate AI media
      if (activeSource === 'ai') {
        try {
          const response = await mediaAPI.getAiMedia({
            mediaType,
            page,
            limit: pageSize,
            search: searchQuery,
          });

          if (response.data?.data?.media) {
            const aiMedia = response.data.data.media.map((item) => ({
              id: item._id,
              name: item.originalName,
              thumbnail: getFullUrl(item.thumbnail) || getFullUrl(item.url),
              url: getFullUrl(item.url),
              duration: item.duration,
              width: item.dimensions?.width || item.width,
              height: item.dimensions?.height || item.height,
              type: item.mediaType === 'image' ? 'images'
                : item.mediaType === 'video' ? 'videos'
                : 'audio',
              category: 'AI Generated',
              description: item.description || '',
              size: item.formattedSize || item.size,
              isUserUpload: false,
              isAiGenerated: true,
              uploadedAt: item.uploadedAt,
            }));

            if (page === 1 && aiMedia.length > 0) {
              allItems.push({
                id: 'header-ai',
                isHeader: true,
                title: 'AI Generated',
                count: response.data.total || aiMedia.length,
              });
            }
            allItems.push(...aiMedia);

            const currentPage = response.data.page || page;
            const totalPages = response.data.pages || 1;
            return { data: allItems, hasMore: currentPage < totalPages };
          }
        } catch (error) {
          console.log('No AI media or not authenticated');
        }
        return { data: allItems, hasMore: false };
      }

      // Stock-only view: paginate stock
      if (activeSource === 'stock') {
        let stockData = { data: [], hasMore: false };

        if (stockMediaService.hasProviders()) {
          try {
            if (searchQuery) {
              stockData = await stockMediaService.search({
                query: searchQuery,
                mediaType,
                page,
                perPage: pageSize,
              });
            } else {
              stockData = await stockMediaService.getPopular({
                mediaType,
                page,
                perPage: pageSize,
              });
            }
          } catch (error) {
            console.error('Stock media service error:', error);
          }
        }

        if (page === 1 && stockData.data.length > 0) {
          allItems.push({
            id: 'header-stock',
            isHeader: true,
            title: 'Stock Media',
            count: stockData.data.length,
          });
        }
        allItems.push(...stockData.data);
        return { data: allItems, hasMore: stockData.hasMore };
      }

      // --- Mixed view ('all' or 'default'): show preview of each source on page 1, then paginate stock ---

      // Fetch user uploads preview (page 1 only)
      if (showUploads && page === 1) {
        try {
          const response = await mediaAPI.getMyMedia({
            mediaType,
            page: 1,
            limit: 10,
            search: searchQuery,
          });

          if (response.data?.data?.media) {
            const userMedia = response.data.data.media.map((item) => ({
              id: item._id,
              name: item.originalName,
              thumbnail: getFullUrl(item.thumbnail) || getFullUrl(item.url),
              url: getFullUrl(item.url),
              duration: item.duration,
              width: item.dimensions?.width || item.width,
              height: item.dimensions?.height || item.height,
              type: activeButton,
              category: 'My Uploads',
              description: item.description || '',
              size: item.formattedSize || item.size,
              isUserUpload: true,
              uploadedAt: item.uploadedAt,
            }));

            if (userMedia.length > 0) {
              allItems.push({
                id: 'header-uploads',
                isHeader: true,
                title: 'My Uploads',
                count: response.data.total || userMedia.length,
              });
              allItems.push(...userMedia);
            }
          }
        } catch (error) {
          console.log('No user uploads or not authenticated');
        }
      }

      // Fetch AI media preview (page 1 only, only for 'all' view)
      if (showAi && page === 1) {
        try {
          const response = await mediaAPI.getAiMedia({
            mediaType,
            page: 1,
            limit: 10,
            search: searchQuery,
          });

          if (response.data?.data?.media) {
            const aiMedia = response.data.data.media.map((item) => ({
              id: item._id,
              name: item.originalName,
              thumbnail: getFullUrl(item.thumbnail) || getFullUrl(item.url),
              url: getFullUrl(item.url),
              duration: item.duration,
              width: item.dimensions?.width || item.width,
              height: item.dimensions?.height || item.height,
              type: item.mediaType === 'image' ? 'images'
                : item.mediaType === 'video' ? 'videos'
                : 'audio',
              category: 'AI Generated',
              description: item.description || '',
              size: item.formattedSize || item.size,
              isUserUpload: false,
              isAiGenerated: true,
              uploadedAt: item.uploadedAt,
            }));

            if (aiMedia.length > 0) {
              allItems.push({
                id: 'header-ai',
                isHeader: true,
                title: 'AI Generated',
                count: response.data.total || aiMedia.length,
              });
              allItems.push(...aiMedia);
            }
          }
        } catch (error) {
          console.log('No AI media or not authenticated');
        }
      }

      // Fetch stock media (paginated, fills remaining space)
      let stockData = { data: [], hasMore: false, total: 0 };

      if (showStock && stockMediaService.hasProviders()) {
        const stockItemsNeeded = pageSize - (page === 1 ? allItems.length : 0);
        const stockPage = page === 1 ? 1 : page - 1;

        try {
          if (searchQuery) {
            stockData = await stockMediaService.search({
              query: searchQuery,
              mediaType,
              page: stockPage,
              perPage: stockItemsNeeded,
            });
          } else {
            stockData = await stockMediaService.getPopular({
              mediaType,
              page: stockPage,
              perPage: stockItemsNeeded,
            });
          }
        } catch (error) {
          console.error('Stock media service error:', error);
        }
      }

      if (page === 1 && stockData.data.length > 0) {
        allItems.push({
          id: 'header-stock',
          isHeader: true,
          title: 'Stock Media',
          count: stockData.data.length,
        });
      }

      allItems.push(...stockData.data);

      return {
        data: allItems,
        hasMore: showStock ? stockData.hasMore : false,
      };
    } catch (error) {
      console.error('Error fetching media data:', error);
      return {
        data: [],
        hasMore: false,
      };
    }
  };

  const handleButtonClick = (buttonId) => {
    setActiveButton(buttonId);
  };

  // Handle item click - either for selection in modal or default behavior
  const handleItemClick = (item) => {
    if (onItemSelect && typeof onItemSelect === 'function') {
      onItemSelect(item);
    }
  };

  // ADDED: Handler for the double-click event
  const handleItemDoubleClick = (item) => {
    if (onItemDoubleClick && typeof onItemDoubleClick === 'function') {
      onItemDoubleClick(item);
    }
  };

  // Handle media deletion
  const handleDeleteMedia = async (item) => {
    try {
      await mediaAPI.deleteMedia(item.id);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete media. Please try again.');
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Detect if this file needs server-side conversion (MOV, AVI, MPEG)
    const needsConversion = /\.(mov|avi|mpeg)$/i.test(file.name) ||
      ['video/quicktime', 'video/x-msvideo', 'video/mpeg'].includes(file.type);

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    try {
      const response = await mediaAPI.uploadMedia(
        file,
        {}, // empty data object
        (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          // When upload reaches 100% and file needs conversion, show converting status
          if (percentCompleted >= 100 && needsConversion) {
            setUploadStatus('converting');
          }
        }
      );

      if (response.data) {
        const media = response.data.data?.media || response.data;
        console.log('Uploaded media:', media);

        // Trigger a refresh by updating the key
        setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus(null);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getAcceptedFileTypes = () => {
    switch (activeButton) {
      case 'images':
        return 'image/*';
      case 'videos':
        return 'video/*';
      case 'audio':
        return 'audio/*';
      case 'gifs':
        return 'image/gif';
      case 'stickers':
        return 'image/*';
      default:
        return '*';
    }
  };

  return (
    <div className='h-full flex flex-col relative'>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        accept={getAcceptedFileTypes()}
      />

      {/* Upload Progress Overlay */}
      {isUploading && <UploadProgress progress={uploadProgress} statusText={uploadStatus} />}

      {/* Button Group - only show when not in modal */}
      {!isModal && (
        <div className='flex gap-1 w-full pl-0.5 pr-1 py-1 mb-3 bg-ag-black-800 border border-ag-black-400 rounded-md'>
          {/* Main media type buttons (only on large screens) */}
          {buttons.map((button) => (
            <button
              key={button.id}
              onClick={() => handleButtonClick(button.id)}
              className={`flex-1 px-1.5 py-1 text-sm rounded-md transition-colors duration-200 ${activeButton === button.id
                ? 'bg-ag-black-400 text-white'
                : 'bg-transparent text-white hover:bg-ag-black-600'
                }`}
            >
              {button.label}
            </button>
          ))}

          {/* Dropdown - shows "More" on large screens, all options on small screens */}
          <div className={isSmallScreen ? 'flex-1' : 'flex-1'}>
            <CustomDropdown
              trigger={
                <button
                  className={`w-full px-1.5 py-1 text-sm rounded-md transition-colors duration-200 flex items-center justify-center gap-1 ${isSmallScreen || isMoreOption
                      ? 'bg-ag-black-400 text-white'
                      : 'bg-transparent text-white hover:bg-ag-black-600'
                    }`}
                >
                  <span className='truncate'>{getMoreLabel()}</span>
                  <ChevronDown size={12} className='flex-shrink-0' />
                </button>
              }
              items={moreOptions}
              onItemClick={(item) => handleButtonClick(item.id)}
              position='bottom-left'
              dropdownClassName='w-full'
            />
          </div>

          {/* Upload button */}
          <button
            onClick={handleUploadClick}
            disabled={isUploading}
            className='px-2 text-sm rounded-md bg-ag-black-700 border border-ag-black-400 hover:bg-ag-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0'
            title={`Upload ${activeButton}`}
          >
            <img
              src={addIcon}
              alt='add icon'
              className='h-4 w-auto hover:invert'
            />
          </button>
        </div>
      )}

      {/* Media Grid using MediaRenderer */}
      {(() => {
        const useMasonry = !isModal && (activeButton === 'images' || activeButton === 'videos' || activeButton === 'gifs' || activeButton === 'stickers');
        return (
          <MediaRenderer
            key={activeButton + activeSource + searchQuery + refreshKey}
            apiUrl={apiUrl}
            dataFetcher={!apiUrl ? fetchMediaData : undefined}
            itemType={activeButton}
            gridCols={isModal ? 4 : 2}
            pageSize={isModal ? 20 : 20}
            dependencies={[activeButton, activeSource, searchQuery]}
            endMessage={`No more ${activeButton} to load`}
            showSize={false}
            showDuration={activeButton !== 'images' && !isModal}
            showType={false}
            className={isModal ? 'h-full' : 'flex-1'}
            onItemClick={handleItemClick}
            onItemDoubleClick={handleItemDoubleClick}
            onDeleteItem={handleDeleteMedia}
            isSelectable={isModal}
            skipDeduplication={activeButton === 'gifs'}
            layout={useMasonry ? 'masonry' : undefined}
            targetRowHeight={130}
            justifiedGap={4}
            getAspectRatio={(item) => {
              if (item.width && item.height) return item.width / item.height;
              if (item.type === 'videos' || item.type === 'video') return 16 / 9;
              return 4 / 3;
            }}
          />
        );
      })()}
    </div>
  );
};

export default React.memo(MediaFiles);